// ─────────────────────────────────────────────────────────────
// LoggingNoticeModal — one-time, informational data-logging notice.
// Shown once per account, gated on the server flag
// `logging_notice_acknowledged`. This is NOT a consent gate — it's a
// friendly heads-up that we log some data about the user's Marie chats
// to improve the product. A single "Got it" button dismisses it.
//
// Gating + the acknowledge round-trip live in ScriptHelperApp (the
// owner of currentUser); this component is presentation only.
// ─────────────────────────────────────────────────────────────

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

interface LoggingNoticeModalProps {
  open: boolean;
  /** Called when the user clicks "Got it". */
  onAcknowledge: () => void;
}

export function LoggingNoticeModal({ open, onAcknowledge }: LoggingNoticeModalProps) {
  return (
    <Dialog
      open={open}
      // Informational, not dismissible by backdrop/escape — the only exit is
      // the single primary button, which is what persists the acknowledgement.
      // (If the network call fails the parent still closes it gracefully.)
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onAcknowledge();
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="logging-notice-title"
      aria-describedby="logging-notice-body"
    >
      <DialogTitle
        id="logging-notice-title"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            flexShrink: 0,
          }}
        >
          <InsightsOutlinedIcon fontSize="small" />
        </Box>
        A quick note
      </DialogTitle>
      <DialogContent>
        <Typography
          id="logging-notice-body"
          variant="body1"
          color="text.secondary"
        >
          We log a little data in the background to make Maps Discover Studio
          better over time. In particular, we look at how Marie writes scripts
          and which scripts run into errors, so she keeps getting smarter at
          writing scripts for you. Nothing for you to do here — just a heads-up.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onAcknowledge} variant="contained" autoFocus>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
